import { NATIVE_CURRENCY_ADDRESS } from "@opensea/app-kit"
import { graphql, useQuery } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  Flex,
  FlexColumn,
  Separator,
  useToasts,
} from "@opensea/ui-kit"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import type { WalletDestination } from "../../../components/DestinationWalletSelect/types"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import type { CurrencyIdentifier } from "../../../providers"
import type { TransactionIdentifier } from "../../../stores/currentTransactionStore"
import { FlowActions, FlowState } from "../../../stores/flowStore"
import type { Item } from "../../../types"
import type { MintFlowState } from "../../flow-state"
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange"
import { useInsufficientFundsActionError } from "../../hooks/useErrors"
import { MintToast } from "../../ToastHandler/MintToast/MintToast"
import { CurrencySelector } from "../CheckoutFlow/components/CheckoutTimeline/CurrencySelector"
import { CurrencySelector_relayerFulfillmentFragment } from "../CheckoutFlow/components/CheckoutTimeline/CurrencySelector.fragment"
import {
  MintActionTimeline,
  MintActionTimelineFragment,
} from "./MintActionTimeline"
import { MintActionTimelineDropFragment } from "./MintActionTimeline/MintActionTimelineDrop.fragment"
import { MintFlowSkeleton } from "./MintFlowSkeleton"
import { MintStatus, MintStatusCollectionFragment } from "./MintStatus"
import { MintTable } from "./MintTable/MintTable"
import {
  MintTableCollectionFragment,
  MintTableDropFragment,
} from "./MintTable/MintTable.fragment"
import type { MintFlowView } from "./types"
import { FindActiveDropStageFragment, findActiveDropStage } from "./utils"

type Props = Pick<MintFlowState, "slug" | "tokenInputs" | "mode">

const MintQuery = graphql(
  `
    query MintQuery($slug: String!) {
      collectionBySlug(slug: $slug) {
        __typename
        ... on Collection {
          imageUrl
          chain {
            identifier
            crossChainBuysSupported
          }
          ...MintTableCollection
          ...MintActionTimeline
          ...MintStatusCollection
          drop {
            identifier {
              contractAddress
              chain {
                identifier
              }
            }
            supportsRelayerFulfillment {
              ...CurrencySelector_relayerFulfillment
            }
            ...MintTableDrop
            ...MintActionTimelineDrop
            ...findActiveDropStage
          }
        }
      }
    }
  `,
  [
    MintTableDropFragment,
    MintTableCollectionFragment,
    MintActionTimelineFragment,
    MintStatusCollectionFragment,
    MintActionTimelineDropFragment,
    FindActiveDropStageFragment,
    CurrencySelector_relayerFulfillmentFragment,
  ],
)

export function MintFlow({ slug, tokenInputs }: Props) {
  const t = useTranslations("wallet.MintFlow")
  const { addCustomToast } = useToasts()
  const [view] = useState<MintFlowView>("mint")
  const [{ data, fetching }] = useQuery({
    query: MintQuery,
    variables: {
      slug,
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })
  const { flow } = useSnapshot(FlowState)
  const mode = (flow as MintFlowState)?.mode
  useEndFlowOnAccountChange()

  const [selectedCurrency, setSelectedCurrency] = useState<
    CurrencyIdentifier | undefined
  >(undefined)
  const [balancesReady, setBalancesReady] = useState(false)
  const [walletDestination, setWalletDestination] = useState<
    WalletDestination | undefined
  >(undefined)

  const _insufficientFundsActionError = useInsufficientFundsActionError()

  const [transactionIdentifier, setTransactionIdentifier] = useState<{
    chain: string
    transactionHash: string
  }>()
  const [isCrossChain, setIsCrossChain] = useState(false)
  const [toAsset, setToAsset] = useState<{
    contractAddress: string
    chain: string
    tokenId: string
  }>()

  const collection =
    data?.collectionBySlug?.__typename === "Collection"
      ? data.collectionBySlug
      : null
  const drop =
    data?.collectionBySlug?.__typename === "Collection"
      ? data.collectionBySlug.drop
      : null

  // Convert simplified tokenInputs to full Items using collection data
  const fullTokenInputs = useMemo((): Item[] => {
    if (!collection?.drop?.identifier.contractAddress) {
      return []
    }

    return tokenInputs.map(input => ({
      chain: {
        identifier: collection.drop?.identifier.chain.identifier || "",
      },
      contractAddress: collection.drop?.identifier.contractAddress || "",
      tokenId: input.tokenId,
      quantity: input.quantity,
    }))
  }, [tokenInputs, collection])

  const totalUsdPrice = useMemo(() => {
    if (!drop) {
      return 0
    }

    const activeStage = findActiveDropStage(drop)

    if (
      activeStage.__typename === "Erc1155SeaDropV2Stage" &&
      activeStage.tokenIdMapping
    ) {
      // For ERC1155 drops, calculate price per token using tokenIdMapping
      return fullTokenInputs.reduce((total, item) => {
        const tokenData = activeStage.tokenIdMapping.get(item.tokenId)
        const usdPrice = tokenData?.price?.usd ?? 0
        return total + usdPrice * item.quantity
      }, 0)
    }
    // For other drop types, use the stage's general price
    const usdPrice =
      activeStage.eligiblePrice?.usd ?? activeStage.price?.usd ?? 0
    const totalQuantity = fullTokenInputs.reduce(
      (sum, item) => sum + item.quantity,
      0,
    )
    return usdPrice * totalQuantity
  }, [drop, fullTokenInputs])

  const formattedItemQuotes = useMemo(() => {
    if (!toAsset) {
      return []
    }

    return fullTokenInputs.map(tokenInput => ({
      quantity: tokenInput.quantity,
      pricePerItem: {
        unit: "0", // Field not needed for receipt
        contractAddress: "0x0000000000000000000000000000000000000000",
      },
      item: {
        tokenId: tokenInput.tokenId,
        contractAddress: tokenInput.contractAddress,
        chain: tokenInput.chain.identifier,
      },
    }))
  }, [toAsset, fullTokenInputs])

  useEffect(() => {
    // Used for analytics.
    FlowActions.setFlowField<MintFlowState>("crossChain", isCrossChain)
  }, [isCrossChain])

  useEffect(() => {
    // Store itemQuotes when toAsset is available
    if (toAsset) {
      FlowActions.setItemQuotes(formattedItemQuotes)
    }
  }, [formattedItemQuotes, toAsset])

  const handleMintComplete = useCallback(
    (
      response: {
        transactionIdentifier: TransactionIdentifier
        isCrossChain: boolean
        toAsset: {
          contractAddress: string
          chain: string
          tokenId: string
        }
      },
      receiptTransactions: TransactionIdentifier[],
    ) => {
      setTransactionIdentifier({
        chain: response.transactionIdentifier.chain,
        transactionHash: response.transactionIdentifier.hash,
      })
      setIsCrossChain(response.isCrossChain)
      setToAsset(response.toAsset)

      // Show toast if mode is minimized
      if (mode === "minimized" && receiptTransactions.length > 0) {
        const { itemQuotes, flow } = FlowState

        addCustomToast(
          () => (
            <MintToast
              flow={flow}
              imageUrl={collection?.imageUrl || ""}
              isCrossChain={response.isCrossChain}
              itemQuotes={itemQuotes}
              transactionIdentifier={{
                chain: response.transactionIdentifier.chain,
                transactionHash: response.transactionIdentifier.hash,
              }}
            />
          ),
          {
            id: "mint-toast",
            duration: Number.POSITIVE_INFINITY,
          },
        )

        FlowActions.endFlow()
      }
    },
    [mode, addCustomToast, collection?.imageUrl],
  )

  if (fetching) {
    return <MintFlowSkeleton />
  }

  if (!collection) {
    throw new Error("Collection not found")
  }

  if (!drop) {
    throw new Error("Drop not found")
  }

  // Transaction is submitted, show status
  if (transactionIdentifier && toAsset) {
    return (
      <MintStatus
        collection={collection}
        isCrossChain={isCrossChain}
        itemQuotes={formattedItemQuotes}
        toAsset={toAsset}
        tokenInputs={fullTokenInputs}
        transactionIdentifier={transactionIdentifier}
        walletDestination={walletDestination}
      />
    )
  }

  return (
    <>
      <BottomSheetHeader>
        <BottomSheetHeaderTitle>{t("mint")}</BottomSheetHeaderTitle>
      </BottomSheetHeader>
      <BottomSheetBody>
        <Flex className="size-full flex-col gap-6 md:flex-row md:gap-10">
          <MintTable
            collection={collection}
            drop={drop}
            tokenInputs={fullTokenInputs}
            view={view}
          />
          <Separator
            className="hidden h-auto md:block"
            orientation="vertical"
          />
          <FlexColumn className="relative w-full min-w-0 justify-end">
            <CurrencySelector
              defaultCurrency={{
                contractAddress: NATIVE_CURRENCY_ADDRESS,
                chainIdentifier: collection.chain.identifier,
              }}
              nftChain={collection.chain.identifier}
              onBalancesReady={() => setBalancesReady(true)}
              onSelect={setSelectedCurrency}
              onWalletSelect={setWalletDestination}
              relayerFulfillment={drop.supportsRelayerFulfillment}
              totalUsdPrice={totalUsdPrice}
              walletDestination={walletDestination}
            />

            <Separator className="my-6" />
            <MintActionTimeline
              collection={collection}
              drop={drop}
              onComplete={handleMintComplete}
              selectedCurrency={selectedCurrency}
              selectedCurrencyLoading={!balancesReady}
              tokenInputs={fullTokenInputs}
              walletDestination={walletDestination}
            />
          </FlexColumn>
        </Flex>
      </BottomSheetBody>
    </>
  )
}
